import { QuantityOrOptionPicker } from "@faire/retailer-visitor-shared/components/QuantityOrOptionPicker/QuantityOrOptionPicker";
import {
  isDiscontinued as isProductOptionDiscontinued,
  isProductOptionInStock,
} from "@faire/retailer-visitor-shared/lib/productOptions";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { useAssignSettingEnablePdpOosEnhancements } from "@faire/retailer-visitor-shared/settings/useSettingEnablePdpOosEnhancements";
import { useSettingEnableProductTileOosEnhancements } from "@faire/retailer-visitor-shared/settings/useSettingEnableProductTileOosEnhancements";
import { ProductAddToCartState } from "@faire/retailer-visitor-shared/views/product/ProductDetails/AddToCart/useProductAddToCartState";
import { buildCaseQuantityText } from "@faire/retailer-visitor-shared/views/product/ProductDetails/buildCaseQuantityText";
import { getProductDetails } from "@faire/retailer-visitor-shared/views/product/utils/productUtils";
import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { NotificationBell } from "@faire/web--source/slate/icons/foundation/NotificationBell";
import { NotificationBellChecked } from "@faire/web--source/slate/icons/foundation/NotificationBellChecked";
import { Row } from "@faire/web--source/slate/Layout/Row";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackBrandCatalogCompletenessOutOfStockActionCategoryImpression } from "@faire/web-api--source/events/brandCatalogCompleteness/actionCategoryImpression/outOfStock";
import { IProductOption } from "@faire/web-api--source/indigofair/data/IProductOption";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import React, { useEffect } from "react";
import styled, { css } from "styled-components";

interface IQuantitySelectorProps {
  productAddToCartState: ProductAddToCartState;
  product: IProductPageResponse.IPdpProduct;
  subscribedBackInStockTokens: string[];
  allowCustomOption?: boolean;
  containingModalElement?: HTMLElement;
  deleteBackInStockNotification: () => void;
}

interface IBackInStockButtonContentProps {
  selectedOption: IProductOption | undefined;
  hasBackorderNotification: boolean;
  subscribedBackInStockTokens: string[];
  emailWhenBackInStock: (includeToast?: boolean) => Promise<void>;
  deleteBackInStockNotification: () => void;
}

export const QuantitySelector: React.FC<IQuantitySelectorProps> = ({
  productAddToCartState,
  product,
  subscribedBackInStockTokens,
  allowCustomOption,
  containingModalElement,
  deleteBackInStockNotification,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    selectedOption,
    selectedQuantity,
    selectedOptionLabel,
    productQuantityOptions,
    perStyleOptionVariations,
    currentStyleHasOnlyPrePacks,
    hasBackorderNotification,
    minimumOrderQuantity,
    unitMultiplier,
    isPrePackProductOption,
    emailWhenBackInStock,
    getCustomOptionValue,
  } = productAddToCartState;

  if (
    selectedOption &&
    !isProductOptionInStock(selectedOption, perStyleOptionVariations)
  ) {
    return (
      <>
        <BackInStockButtonContent
          hasBackorderNotification={hasBackorderNotification}
          selectedOption={selectedOption}
          subscribedBackInStockTokens={subscribedBackInStockTokens}
          deleteBackInStockNotification={deleteBackInStockNotification}
          emailWhenBackInStock={emailWhenBackInStock}
        />
        <Spacer size={5} />
      </>
    );
  }

  const isDiscontinued = isProductOptionDiscontinued(product, selectedOption);

  return (
    <div style={{ width: "100%" }} tabIndex={2} role="button">
      <Row justify="space-between">
        <Typography>
          {quantityLabel(
            minimumOrderQuantity,
            unitMultiplier,
            isPrePackProductOption,
            strictLocalize
          )}
        </Typography>
        <Typography
          data-test-id={`${
            currentStyleHasOnlyPrePacks ? "prepack-" : ""
          }product-case-qty`}
        >
          {buildCaseQuantityText(
            getProductDetails(product),
            selectedOption,
            strictLocalize
          )}
        </Typography>
      </Row>
      <Spacer size={4} />
      <QuantityOrOptionPicker
        value={selectedQuantity?.toString()}
        onSelect={({ value }) =>
          productAddToCartState.setSelectedQuantity(parseInt(value))
        }
        options={productQuantityOptions}
        placeholder={isDiscontinued ? "0" : selectedOptionLabel}
        disabled={isDiscontinued}
        dataTestId="QuantityOrOptionPicker"
        containingElement={containingModalElement}
        shouldUseCustomOption={allowCustomOption}
        getCustomOptionLabel={productAddToCartState.productOptionQuantityLabel}
        getCustomOptionValue={getCustomOptionValue}
      />
      <Spacer height="1x" />
    </div>
  );
};

const quantityLabel = (
  minimumOrderQuantity: number,
  unitMultiplier: number,
  isPrePackProductOption: boolean,
  strictLocalize: StrictLocalizeFunction
) => {
  let label = isPrePackProductOption
    ? strictLocalize({
        defaultMessage: "Pack Quantity",
        description: { text: "The quantity within a predefined package" },
      })
    : strictLocalize({
        defaultMessage: "Item Quantity",
        description: { text: "The quantity of the product option" },
      });

  if (minimumOrderQuantity !== unitMultiplier) {
    label +=
      " " +
      strictLocalize(
        {
          defaultMessage: "(Minimum {minimumOrderQuantityNumber})",
          description: {
            text: "The minimum quantity for the product to be purchased",
          },
        },
        {
          minimumOrderQuantityNumber: minimumOrderQuantity,
        }
      );
  }
  return label;
};

const BackInStockButtonContent: React.FC<IBackInStockButtonContentProps> = ({
  hasBackorderNotification,
  selectedOption,
  subscribedBackInStockTokens,
  emailWhenBackInStock,
  deleteBackInStockNotification,
}) => {
  const retailerToken = getRetailer()?.token;
  const assignSettingEnablePdpOosEnhancements =
    useAssignSettingEnablePdpOosEnhancements();
  const enableProductTileOosEnhancements =
    useSettingEnableProductTileOosEnhancements();

  const viewRef = useDoActionWhenVisible(() => {
    if (enableProductTileOosEnhancements) {
      trackBrandCatalogCompletenessOutOfStockActionCategoryImpression(
        retailerToken ?? "",
        selectedOption?.product_token ?? "",
        selectedOption?.token ?? ""
      );
    }
  });

  useEffect(() => {
    assignSettingEnablePdpOosEnhancements();
  }, [assignSettingEnablePdpOosEnhancements]);

  const isSubscribedBackInStock =
    selectedOption !== undefined &&
    selectedOption.token !== undefined &&
    subscribedBackInStockTokens !== undefined &&
    subscribedBackInStockTokens.includes(selectedOption.token);

  if (hasBackorderNotification || isSubscribedBackInStock) {
    return (
      <OutOfStockEmail
        variant={enableProductTileOosEnhancements ? "primary" : "secondary"}
        onClick={() => deleteBackInStockNotification()}
        data-test-id="notified-button-on-pdp"
      >
        <StyledNotificationBellChecked ref={viewRef} />
        <StrictLocalMsg
          defaultMessage="You’ll be notified when restocked"
          description={{
            text: "Has scheduled an email for the retailer when the product is back in stock",
            img: "https://cdn.faire.com/fastly/c60f07564021abdda414567d8b95d79336a36d84b16be7eba11de82a6c24d2c0.png",
          }}
        />
      </OutOfStockEmail>
    );
  } else {
    return (
      <OutOfStockEmail
        variant={enableProductTileOosEnhancements ? "primary" : "secondary"}
        onClick={() => emailWhenBackInStock(true)}
        data-test-id="notify-me-button-on-pdp"
      >
        <StyledNotificationBell ref={viewRef} />
        <StrictLocalMsg
          defaultMessage="Notify me when back in stock"
          description={{
            text: "Click to subscribe to an email that will let you know when the product is back in stock",
            img: "https://cdn.faire.com/fastly/bc2b35cd10c1482b1cbf466fbdac3b341cbcbfe3663d14ba684577a4b03370a2.png",
          }}
        />
      </OutOfStockEmail>
    );
  }
};

const NotifyMeIconStyles = css`
  fontsize: 15px;
  margin: 7px;
  color: inherit;
`;

const OutOfStockEmail = styled(Button)`
  width: 100%;
`;

const StyledNotificationBellChecked = styled(NotificationBellChecked)`
  ${NotifyMeIconStyles};
`;

const StyledNotificationBell = styled(NotificationBell)`
  ${NotifyMeIconStyles};
`;
